import React from 'react';
import { useLanguage } from './context/LanguageContext';

const content = {
  de: {
    subtitle: 'Ein deutscher Familienname und seine Geschichte',
    origin_title: 'Herkunft & Bedeutung',
    origin_text_1: 'Kornbrust ist ein deutscher Nachname mit relativ seltener Verbreitung — in Deutschland gibt es nur ca. 48–100 Träger.',
    origin_text_2: 'Die zuverlässigste Erklärung kommt aus der deutschen Namenforschung: Kornbrust ist eine <em>berufsbezogene Namensvariante</em> und hängt eng mit „Kornprobst" oder „Probst" zusammen.',
    meaning_title: 'Wortbestandteile',
    meaning_korn_title: '„Korn"',
    meaning_korn_text: 'Steht für Getreide (Roggen, Weizen etc.) — der landwirtschaftliche Bezug des Namens.',
    meaning_brust_title: '„Brust"',
    meaning_brust_text: 'Leitet sich wahrscheinlich von mittelhochdeutsch/althochdeutsch „bruost" ab, das im Kontext von Amtsbezeichnungen „Brauer" oder „Braumeister" bedeuten kann.',
    interpretation_title: 'Deutungen',
    interpretation_text_1: 'Insgesamt handelt es sich um eine Bezeichnung für einen <em>Brauer</em>, der speziell Kornbier (Getreidebier) herstellte oder damit zu tun hatte.',
    interpretation_text_2: 'Eine andere Deutung sieht darin eine volksetymologische Umformung von „Kornprobst" (Probst = Vorsteher, Aufseher, z. B. eines Klosters oder einer kirchlichen Einrichtung, kombiniert mit Korn als landwirtschaftlichem Bezug). Das „Brust" wäre dann eine lautlich angepasste oder umgedeutete Form.',
    interpretation_text_3: 'Es gibt auch vereinzelte Interpretationen als Wohnstätten- oder Hofname, aber die berufliche Erklärung (Brauer/Probst) gilt als wahrscheinlicher.',
    distribution_title: 'Verbreitung',
    distribution_text_1: 'Der Name ist hauptsächlich in Deutschland beheimatet, mit historischen Spuren besonders im <strong>hessischen</strong> und <strong>saarländischen</strong> Raum (z. B. Verbindungen zu St. Wendel im Saarland, wo es Künstler wie Leo Kornbrust gab).',
    distribution_text_2: 'Im 19./frühen 20. Jahrhundert wanderten einige Träger nach <strong>Amerika</strong> aus (vor allem USA).',
    distribution_link: 'Namensverbreitung auf Geneanet ansehen',
    summary_title: 'Zusammenfassung',
    summary_text: 'Kornbrust ist ein typisch deutscher Berufs- bzw. Amtsname, der höchstwahrscheinlich auf einen Getreidebrauer oder eine mit „Korn" und einem Aufseheramt verbundene Person zurückgeht — ähnlich wie andere Namen wie Kornmüller, Bierbrauer oder Probst.',
    footer: 'Familie Kornbrust',
  },
  en: {
    subtitle: 'A German family name and its history',
    origin_title: 'Origin & Meaning',
    origin_text_1: 'Kornbrust is a German surname with relatively rare distribution — there are only about 48–100 bearers in Germany.',
    origin_text_2: 'The most reliable explanation comes from German onomastics: Kornbrust is an <em>occupation-related name variant</em> closely connected to "Kornprobst" or "Probst".',
    meaning_title: 'Name Components',
    meaning_korn_title: '"Korn"',
    meaning_korn_text: 'Stands for grain (rye, wheat, etc.) — the agricultural reference in the name.',
    meaning_brust_title: '"Brust"',
    meaning_brust_text: 'Likely derives from Middle High German/Old High German "bruost", which in the context of official titles could mean "brewer" or "master brewer".',
    interpretation_title: 'Interpretations',
    interpretation_text_1: 'Overall, it is a designation for a <em>brewer</em> who specifically produced grain beer or was involved in grain brewing.',
    interpretation_text_2: 'Another interpretation sees it as a folk-etymological transformation of "Kornprobst" (Probst = provost, overseer, e.g. of a monastery or ecclesiastical institution, combined with Korn as an agricultural reference). The "Brust" would then be a phonetically adapted or reinterpreted form.',
    interpretation_text_3: 'There are also isolated interpretations as a homestead or farm name, but the occupational explanation (brewer/provost) is considered more likely.',
    distribution_title: 'Distribution',
    distribution_text_1: 'The name is primarily found in Germany, with historical traces especially in the <strong>Hessian</strong> and <strong>Saarland</strong> regions (e.g. connections to St. Wendel in the Saarland, where artists like Leo Kornbrust lived).',
    distribution_text_2: 'In the 19th/early 20th century, some bearers emigrated to <strong>America</strong> (mainly the USA).',
    distribution_link: 'View name distribution on Geneanet',
    summary_title: 'Summary',
    summary_text: 'Kornbrust is a typical German occupational or official name, most likely referring to a grain brewer or a person connected to "grain" and an overseer position — similar to other names like Kornmüller, Bierbrauer, or Probst.',
    footer: 'The Kornbrust Family',
  },
};

function Home() {
  const { lang, setLang } = useLanguage();
  const c = content[lang];

  return (
    <div className="App">
      {/* Header */}
      <header className="header">
        <div className="header-inner">
          <div className="site-title"><span>Korn</span>brust</div>
          <div className="lang-switcher">
            <button className={`lang-btn ${lang === 'de' ? 'active' : ''}`} onClick={() => setLang('de')} title="Deutsch">
              <svg viewBox="0 0 640 480" className="flag-icon">
                <rect width="640" height="160" fill="#000"/>
                <rect y="160" width="640" height="160" fill="#D00"/>
                <rect y="320" width="640" height="160" fill="#FFCE00"/>
              </svg>
            </button>
            <button className={`lang-btn ${lang === 'en' ? 'active' : ''}`} onClick={() => setLang('en')} title="English">
              <svg viewBox="0 0 640 480" className="flag-icon">
                <rect width="640" height="480" fill="#012169"/>
                <path d="M75 0l244 181L562 0h78v62L400 241l240 178v61h-80L320 302 82 480H0v-60l239-178L0 64V0h75z" fill="#fff"/>
                <path d="M424 281l216 159v40L369 281h55zm-184 20l6 35L54 480H0l240-179zM640 0v3L391 191l2-44L590 0h50zM0 0l239 176h-60L0 42V0z" fill="#C8102E"/>
                <path d="M241 0v480h160V0H241zM0 160v160h640V160H0z" fill="#fff"/>
                <path d="M0 193v96h640v-96H0zM273 0v480h96V0h-96z" fill="#C8102E"/>
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="hero">
        <h1><span>Korn</span>brust</h1>
        <p className="subtitle">{c.subtitle}</p>
      </section>

      {/* Origin */}
      <section className="section">
        <h2 className="section-title">
          <div className="section-icon">
            <svg viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
          </div>
          {c.origin_title}
        </h2>
        <div className="card">
          <p>{c.origin_text_1}</p>
          <p dangerouslySetInnerHTML={{ __html: c.origin_text_2 }} />
        </div>
      </section>

      {/* Meaning */}
      <section className="section">
        <h2 className="section-title">
          <div className="section-icon">
            <svg viewBox="0 0 24 24"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
          </div>
          {c.meaning_title}
        </h2>
        <div className="info-grid">
          <div className="info-card">
            <h3>{c.meaning_korn_title}</h3>
            <p>{c.meaning_korn_text}</p>
          </div>
          <div className="info-card">
            <h3>{c.meaning_brust_title}</h3>
            <p>{c.meaning_brust_text}</p>
          </div>
        </div>
      </section>

      {/* Interpretations */}
      <section className="section">
        <h2 className="section-title">
          <div className="section-icon">
            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          </div>
          {c.interpretation_title}
        </h2>
        <div className="card">
          <p dangerouslySetInnerHTML={{ __html: c.interpretation_text_1 }} />
          <p>{c.interpretation_text_2}</p>
          <p>{c.interpretation_text_3}</p>
        </div>
      </section>

      <div className="divider"><hr /></div>

      {/* Distribution */}
      <section className="section">
        <h2 className="section-title">
          <div className="section-icon">
            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
          </div>
          {c.distribution_title}
        </h2>
        <div className="card">
          <p dangerouslySetInnerHTML={{ __html: c.distribution_text_1 }} />
          <p dangerouslySetInnerHTML={{ __html: c.distribution_text_2 }} />
          <a href="https://de.geneanet.org/familiennamen/KORNBRUST" target="_blank" rel="noopener noreferrer" className="ext-link">
            <svg viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            {c.distribution_link}
          </a>
        </div>
      </section>

      {/* Summary */}
      <section className="section">
        <h2 className="section-title">
          <div className="section-icon">
            <svg viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          </div>
          {c.summary_title}
        </h2>
        <div className="highlight">
          <p>{c.summary_text}</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        &copy; {new Date().getFullYear()} {c.footer}
      </footer>
    </div>
  );
}

export default Home;
